import React from "react";
import { Card, Button, Col, Row, Container, Tooltip, OverlayTrigger } from "react-bootstrap";
import { Link } from "react-router-dom";
import { CCAssessmentType, CCEnrollment } from "../../../Physician/Models/CCEnrollment";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";
import { AvailableExamination } from "../Models/AvailableExamination";

interface IProps {
    examination: AvailableExamination;
    applyLinkTo: any;
    currentCCEnrollment: CCEnrollment;
    isFullyLapsed: boolean;
}

export function AvailableExaminationCard(props: IProps) {
    const currentDate = new Date().setHours(0,0,0,0);
    const isLateApplication = currentDate.valueOf() > getDateWithOffset(props.examination.applicationDeadLineDate).valueOf() ;
    const applicationAvailableDate = getDateWithOffset(props.examination.applicationAvailableDate);
    const nextApplicationAvailableDate = props.examination.nextApplicationAvailableDate ? getDateWithOffset(props.examination.nextApplicationAvailableDate).toLocaleDateString() : "TBA"
    const isAvailable = applicationAvailableDate.valueOf() <= currentDate.valueOf();

    const filteredExamCertifications = () => {
        const ccEnrollmentExamCertificates = props.currentCCEnrollment?.activeEnrollmentCertificates.filter(_ => _.ccAssessmentType == CCAssessmentType.Exam);
        if(ccEnrollmentExamCertificates == null) return null;
        var ccEnrollmentExamCertificationIds = ccEnrollmentExamCertificates.map(_ => _.certification_Key);
        var filteredCertifications = props.examination.availableModuleCertifications.filter(_ => ccEnrollmentExamCertificationIds.indexOf(_.certificationId) != -1);
        return filteredCertifications;
    };

    if (!props.examination) return <AbpnSpinner></AbpnSpinner>
    return (
        <Card border="pp-secondary-grey" className="mb-4" key={props.examination.title}>
            <h5 className="mt-4 ml-4 font-weight-bold text-pp-primary">
                {props.examination.title}
            </h5>
            { props.isFullyLapsed && props.examination.isArticleBased &&
                <p className="mt-4 ml-4 font-weight-bold pp-danger-text">Unlike the traditional 10-Year Continuing Certification exam, which provides a quick pass/fail decision, the ABCC assessment pathway does not issue a final performance decision until the end of the 3-year assessment cycle.</p>
            }
            <Card.Body className="p-2">
                <Container fluid >
                    <Row>
                        <Col className="mt-2">
                            <Card key={props.examination.applicationDeadLineDate} className="h-100 d-flex flex-column justify-content-end border-0">
                                <h6 className="font-weight-bolder mb-1">
                                    Application Deadline
                                    <OverlayTrigger
                                        placement={"right"}
                                        overlay={<Tooltip id="applicationDeadline" bsPrefix="pp-tooltip">
                                            Application deadlines are at 11:59 p.m. CST, after which the late fee will be applied. 
                                        </Tooltip>}>
                                        <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                    </OverlayTrigger>
                                </h6>
                                <Card.Title>
                                    {isAvailable ? getDateWithOffset(props.examination.applicationDeadLineDate).toLocaleDateString() : "TBA"}
                                </Card.Title>
                            </Card>
                        </Col>
                        <Col className="mt-2">
                            <Card key={props.examination.startDate} className="h-100 d-flex flex-column justify-content-end border-0">
                                <h6 className="font-weight-bolder mb-1">
                                    Exam Start
                                </h6>
                                <Card.Title>
                                    {props.examination.startDate ? getDateWithOffset(props.examination.startDate).toLocaleDateString() : ""}
                                </Card.Title>
                            </Card>
                        </Col>
                        <Col className="mt-2">
                            <Card key={props.examination.endDate} className="h-100 d-flex flex-column justify-content-end border-0">
                                <h6 className="font-weight-bolder mb-1">
                                    Exam End
                                        </h6>
                                <Card.Title>
                                    {props.examination.endDate ? getDateWithOffset(props.examination.endDate).toLocaleDateString() : ""}
                                </Card.Title>
                            </Card>
                        </Col>

                        <Col className="mt-2">
                            <Card key={props.examination.examFee} className="h-100 d-flex flex-column justify-content-end border-0">
                                <h6 className="font-weight-bolder mb-1">
                                    Fee
                                        </h6>
                                <Card.Title>
                                    {isAvailable ? `$${props.examination.examFee}`: "TBA"}
                                </Card.Title>
                            </Card>
                        </Col>
                        <Col className="mt-2">
                            {isLateApplication ?
                            <Card key={props.examination.lateFee} className="h-100 d-flex flex-column justify-content-end border-0">
                                <h6 className="pp-late-fee-label">
                                    <span className="font-weight-bolder mb-1 text-pp-primary">Late Fee</span>
                                    <OverlayTrigger
                                        placement={"top"}
                                        overlay={<Tooltip id="lateFeeInfo" bsPrefix="pp-tooltip">
                                                    This exam is still available for an additional ${props.examination.lateFee} as the application deadline has passed.                                                
                                                </Tooltip>}>
                                        <i className="las la-info-circle ml-1 la-lg"></i>
                                    </OverlayTrigger>
                                </h6>                              
                                <Card.Title>
                                    <span className="text-pp-primary">{isAvailable ? `$${props.examination.lateFee}` : "TBA"}</span>                           
                                </Card.Title>
                            </Card> : 
                            <Card key={props.examination.lateFee} className="h-100 d-flex flex-column justify-content-end border-0">
                                <h6 className="font-weight-bolder mb-1">
                                    Late Fee
                                        </h6>
                                <Card.Title>
                                    {isAvailable ? `$${props.examination.lateFee}` : "TBA"}
                                </Card.Title>
                            </Card>
                            }                         
                        </Col>
                        <Col className="mt-2">
                            <Card key={props.examination.nextApplicationAvailableDate} className="h-100 d-flex flex-column justify-content-end border-0">
                                <h6 className="font-weight-bolder mb-1">
                                    {isAvailable ? "Next Available Application" : "Application Available"}
                                    </h6>
                                <Card.Title>
                                    {isAvailable ? nextApplicationAvailableDate : getDateWithOffset(props.examination.applicationAvailableDate).toLocaleDateString()}
                                </Card.Title>
                            </Card>
                        </Col>
                        {
                            props.examination.availableModuleCertifications.length > 0 && 
                            <Col className="mt-2">
                                <Card key={props.examination.nextApplicationAvailableDate} className="h-100 d-flex flex-column justify-content-end border-0">
                                    {filteredExamCertifications() != null &&
                                    <div>
                                        <h6 className="font-weight-bolder mb-1">
                                            Available Certifications
                                        </h6>
                                        {
                                            filteredExamCertifications().map(_ => {
                                                return <Card.Title>{_.certificationName}</Card.Title>
                                            })
                                        }
                                        </div>
                                    }
                                </Card>
                            </Col>
                        }
                        <Col className="mt-2">
                        </Col>
                        <br/>
                        <Col bsPrefix="ml-3 mb-3 pp-col-button pp-align-self-end">
                        </Col>
                    </Row>
                    <Row className="mt-4 mb-4">
                        <Col>
                            <a href={props.examination.infoUrl} target="_blank">Information for Applicants</a>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {isAvailable ? 
                                <Card bsPrefix="border-0 pp-btn-card">
                                    <Link to={props.applyLinkTo}><Button variant="pp-primary">Apply</Button></Link>
                                </Card>
                                : null
                            }
                        </Col>
                    </Row>
                </Container>
            </Card.Body>
        </Card>
    );
}

export default AvailableExaminationCard;